import axios, { InternalAxiosRequestConfig } from 'axios';
import Cookies from 'js-cookie';
import { getErrorMessage, getErrorStatus, isOldToken } from '@common/utils';
import { ERROR_MESSAGES } from '@common/constants';
import * as Sentry from '@sentry/react';
import { StatusCodes } from 'http-status-codes';
import { HttpMethod } from '@/enums';
import Routes from '@/router/routes';
import useAuthStore from '@/store/AuthStore';
import useErrorStore from '@/store/ErrorStore';

// Flag to prevent multiple concurrent unauthorized redirects
let isRedirectingToUnauthorized = false;

const axiosInstance = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

axiosInstance.interceptors.request.use(async (config) => {
  const token = Cookies.get('token');
  const authStore = useAuthStore.getState();
  // Check for old token format
  const isOldJwtToken = token ? isOldToken(token) : false;
  if (isOldJwtToken) {
    authStore.logout();
  }

  if (config.headers.Authorization) {
    return {
      ...config,
      headers: {
        ...config.headers,
        Authorization: `Bearer ${token}`,
      },
    } as InternalAxiosRequestConfig;
  }

  // eslint-disable-next-line no-param-reassign
  config.headers.Authorization = token ? `Bearer ${token}` : '';

  // Add client IP to all requests
  const clientIP = localStorage.getItem('client_ip');
  if (clientIP) {
    config.headers['x-client-ip'] = clientIP;
  }

  return config;
});

axiosInstance.interceptors.response.use(
  async (response) => response,
  (error) => {
    // Capture API errors with rich context
    Sentry.withScope((scope) => {
      // Add request context
      scope.setTag('request_url', error.config?.url || 'unknown');
      scope.setTag('request_method', error.config?.method || 'unknown');

      // Add response context
      const status = error?.response?.status;
      scope.setTag('response_status', status);

      // Add user/session context
      const organizationId = Cookies.get('main_organization');
      const workspaceId = Cookies.get('fallback_workspace');

      if (organizationId) {
        scope.setTag('organization_id', organizationId);
      }
      if (workspaceId) {
        scope.setTag('workspace_id', workspaceId);
      }

      // Add browser/device context
      scope.setContext('browser_info', {
        userAgent: navigator.userAgent,
        language: navigator.language,
        screenSize: `${window.screen.width}x${window.screen.height}`,
        viewportSize: `${window.innerWidth}x${window.innerHeight}`,
        connection: navigator.connection ? navigator.connection.effectiveType : 'unknown',
      });

      // Set appropriate error category
      scope.setTag('error_type', 'dashboard_api_error');

      // Add request data when available (sanitize sensitive information)
      if (error.config?.data) {
        try {
          const requestData = JSON.parse(error.config.data);
          // Redact sensitive fields
          const sanitizedData = { ...requestData };
          ['password', 'token', 'secret', 'key'].forEach((field) => {
            if (sanitizedData[field]) sanitizedData[field] = '[REDACTED]';
          });
          scope.setContext('request_data', sanitizedData);
        } catch (e) {
          // Not JSON data or parsing error
        }
      }

      // Custom fingerprinting based on error type
      const url = error.config?.url || '';
      const endpoint = url.split('/').slice(-2).join('/');

      // Group API errors by endpoint and status
      scope.setFingerprint(['dashboard-api-error', `endpoint-${endpoint}`, `status-${status}`]);

      // Capture the exception with enhanced context
      Sentry.captureException(error);
    });

    const authStore = useAuthStore.getState();
    const errorStore = useErrorStore.getState();

    // Handle BAD_REQUEST, NOT_FOUND and INTERNAL_SERVER_ERROR errors for critical endpoints
    const status = getErrorStatus(error);
    const url = error.config?.url;

    // Check if API endpoint is for organization or workspace resources
    const isOrgOrWorkspaceEndpoint = !!(
      url?.includes('/organization') || url?.includes('/workspace')
    );
    const isGetRequest = error.config?.method === HttpMethod.GET;
    const isNotFoundStatus = status === StatusCodes.NOT_FOUND;

    if (isOrgOrWorkspaceEndpoint && isNotFoundStatus && isGetRequest) {
      errorStore.setShowError(true);
    }

    if (error.response && error.response.status === StatusCodes.UNAUTHORIZED) {
      const is2FAOperation = error.config?.url?.includes('enable-2fa');
      const is2FARequired = error.config?.url?.includes('verify-2fa');
      if (ERROR_MESSAGES.TOKEN_EXPIRED === error.response?.data?.message) {
        window.location.href = Routes.session_expired.path;
        localStorage.setItem('sessionExpiredRedirectTo', window.location.pathname);
        Cookies.remove('token');
        return;
      }

      if (!authStore.isLoggingOut && !is2FAOperation && !is2FARequired) {
        authStore.logout();
      }
    }

    const message = getErrorMessage(error);
    const isForbidden = status === StatusCodes.FORBIDDEN;

    // Only some messages should be passed as a URL query param
    const messagesAllowedInQueryParam = [
      ERROR_MESSAGES.ORGANIZATION_INVITE_NOT_ACCEPTED,
      ERROR_MESSAGES.INVALID_ROLE,
      ERROR_MESSAGES.NOT_ORGANIZATION_MEMBER,
    ];

    const shouldIncludeMessageParam = messagesAllowedInQueryParam.includes(message);

    const buildUnauthorizedRedirectUrl = (includeMessage: boolean) => {
      const messageParam = includeMessage ? `?message=${encodeURIComponent(message)}` : '';
      return `${Routes.unauthorized.path}${messageParam}`;
    };

    if (isForbidden && isGetRequest && !isRedirectingToUnauthorized) {
      isRedirectingToUnauthorized = true;
      const redirectUrl = buildUnauthorizedRedirectUrl(shouldIncludeMessageParam);
      window.location.href = redirectUrl;
    }
    return Promise.reject(error.response || error);
  },
);

export default axiosInstance;
